import { IpaType, isTone } from "../../phonemes/ipa";
import { ipaToSynthSymbols } from "../../speak/exact";
import { playAudio } from "../../speak/playAudio";
import { speakPhonemes } from "../../speak/speakPhonemes";

export type LinguistOptions =
  | 'JE'  /** J. Esling */
  | 'JH'  /** J. House */
  | 'PL'  /** P. Ladefoged */
  | 'JW'  /** J. Wells */
  ;
const ipaBaseURL = 'https://www.internationalphoneticassociation.org/IPAcharts/common_files/sounds';

/** true if there's an audio sample for this ipa (external mp3 or synthesizer) */
export function hasSample(ipa: IpaType): boolean {
  return (ipa.length === 1 && !unsupportedSingle.includes(ipa) && ipa !== '•')
    || (ipa.length === 2 && supportedDouble.includes(ipa))
    || getWikiUrl(ipa) !== ''
    || isSupportedTone(ipa);
}

/** play external mp3s for supported IPA, either from IPA.org or Wikipedia, or speech synthesizer for tones */
export function speakSample(ipa: IpaType, linguist?: LinguistOptions): void {
  if (isSupportedTone(ipa)) {
    speakPhonemes(`aː${ipa}`);
    return;
  }

  let url: string = '';
  linguist = linguist ?? 'PL';
  // e.g. https://www.internationalphoneticassociation.org/IPAcharts/inter_chart_2018/sounds/JE/0280.mp3
  if (ipa.length === 1 && !unsupportedSingle.includes(ipa)) {
    const a = toPaddedHex(ipa, 0);
    url = `${ipaBaseURL}/${linguist}/${a}.mp3`;
  }
  if (ipa.length === 2 && supportedDouble.includes(ipa)) {
    const a = toPaddedHex(ipa, 0);
    const b = toPaddedHex(ipa, 1);
    url = `${ipaBaseURL}/${linguist}/${a}_${b}.mp3`;
  }
  if (url === '') {
    url = getWikiUrl(ipa);
  }

  playAudio(url);
}

/** convert a character into a 4-digit hex string, padded with 0's */
function toPaddedHex(ipa: IpaType, i: number): string {
  const code = ipa.charCodeAt(i);
  const hex = Number(code).toString(16).toUpperCase();
  return hex.length === 2 ? `00${hex}` : hex.length === 3 ? `0${hex}` : hex;
}

function isSupportedTone(ipa: IpaType): boolean {
  return isTone(ipa) && ipaToSynthSymbols[ipa] !== undefined;
}

const unsupportedSingle = [
  'ɝ', 'ɝ', 'ɚ',  // vowel
  'ȶ', 'ȵ', 'ʆ', 'ʓ', 'ȴ',  // alveolo-palatals
  'ꞎ', '𝼄',  // lateral-fricatives
  'ɫ',  // velarized "dark" l, listed in wpSymbolToURL
  'ⱱ', 'ᴅ',  // tap/flap
  'ᶑ',  // implosive
  '‼',  // click
  '˥', '˦', '˧', '˨', '˩' // tones
];

const supportedDouble = [
  'pʼ', 'tʼ', 'kʼ', 'sʼ',
];

/**
 * convert a symbol to a Wikipedia sound link,
 * see: https://en.wikipedia.org/wiki/IPA_consonant_chart_with_audio,
 * https://en.wikipedia.org/wiki/IPA_vowel_chart_with_audio
 */
function getWikiUrl(ipa: IpaType) {
  // first remove tie-bars, zero-width-joiners
  ipa = ipa.replace(/[\u035C|\u0361|\u200D|\u203f]/g, '');
  if (wpSymbolToURL[ipa] === undefined) {
    return '';
  }
  if (wpSymbolToURL[ipa].startsWith('http')) {
    return wpSymbolToURL[ipa];
  }
  return wpBaseURL + wpSymbolToURL[ipa];
}

const wpSymbolToURL: Record<IpaType, string> = {
  t̪: '/d/dc/Voiceless_dental_stop.ogg/Voiceless_dental_stop.ogg.mp3',
  d̪: '/1/1c/Voiced_dental_stop.ogg/Voiced_dental_stop.ogg.mp3',
  m̥: '/c/c3/Voiceless_Bilabial_Nasal.ogg/Voiceless_Bilabial_Nasal.ogg.mp3',
  ɱ̥: '/7/76/Voiceless_labiodental_nasal.ogg/Voiceless_labiodental_nasal.ogg.mp3',
  n̥: '/8/82/Voiceless_Alveolar_Nasal.ogg/Voiceless_Alveolar_Nasal.ogg.mp3',
  ɳ̥: '/d/d0/Voiceless_retroflex_nasal.wav/Voiceless_retroflex_nasal.wav.mp3',
  ɲ̥: '/b/bc/Voiceless_palatal_nasal.ogg/Voiceless_palatal_nasal.ogg.mp3',
  ŋ̥: '/c/c7/Voiceless_velar_nasal.wav/Voiceless_velar_nasal.wav.mp3',
  r̥: '/1/1e/Voiceless_alveolar_trill.ogg/Voiceless_alveolar_trill.ogg.mp3',
  ʀ̥: '/8/8d/Voiceless_uvular_trill.ogg/Voiceless_uvular_trill.ogg.mp3',
  r̝: 'https://upload.wikimedia.org/wikipedia/commons/c/c7/Raised_alveolar_non-sonorant_trill.ogg',
  ⱱ: '/2/2c/Labiodental_flap.ogg/Labiodental_flap.ogg.mp3',
  ⱱ̟: '/7/7c/Voiced_bilabial_flap.wav/Voiced_bilabial_flap.wav.mp3',
  // ɾ̥: '/c/cb/Voiceless_alveolar_tap.wav/Voiceless_alveolar_tap.wav.mp3', // sounds odd
  ɢ̆: '/b/b7/Voiced_uvular_tap.wav/Voiced_uvular_tap.wav.mp3',
  ʡ̮: '/f/f1/Epiglottal_flap.oga/Epiglottal_flap.oga.mp3',
  β̞: '/1/11/Bilabial_approximant.ogg/Bilabial_approximant.ogg.mp3',
  ð̞: '/1/11/Voiced_dental_approximant.ogg/Voiced_dental_approximant.ogg.mp3',
  ʁ̞: '/1/19/Voiced_Uvular_Approximant.ogg/Voiced_Uvular_Approximant.ogg.mp3',
  l̪: '/f/f8/Voiced_dental_lateral_approximant.ogg/Voiced_dental_lateral_approximant.ogg.mp3',
  ɫ: '/d/d3/Velarized_alveolar_lateral_approximant.ogg/Velarized_alveolar_lateral_approximant.ogg.mp3',
  ꞎ: '/5/54/Voiceless_retroflex_lateral_fricative.ogg/Voiceless_retroflex_lateral_fricative.ogg.mp3',
  '𝼄': '/9/99/Voiceless_velar_lateral_fricative.ogg/Voiceless_velar_lateral_fricative.ogg.mp3',

  kp: '/5/5f/Voiceless_labial-velar_plosive.ogg/Voiceless_labial-velar_plosive.ogg.mp3',
  ɡb: '/1/1f/Voiced_labial-velar_plosive.ogg/Voiced_labial-velar_plosive.ogg.mp3',
  ŋm: '/9/99/Labial-velar_nasal_stop.ogg/Labial-velar_nasal_stop.ogg.mp3',
  ɠ̊ɓ̥: '/3/36/Voiceless-labial%E2%80%93velar-implosive.ogg/Voiceless-labial%E2%80%93velar-implosive.ogg.mp3',
  ɠɓ: '/3/37/Voiced-labial-velar-implosive.ogg/Voiced-labial-velar-implosive.ogg.mp3',

  pɸ: '/4/4a/Voiceless_bilabial_affricate.ogg/Voiceless_bilabial_affricate.ogg.mp3',
  bβ: '/b/b4/Voiced_bilabial_affricate.wav/Voiced_bilabial_affricate.wav.mp3',
  pf: '/8/83/Voiceless_labiodental_affricate.ogg/Voiceless_labiodental_affricate.ogg.mp3',
  bv: '/3/33/Voiced_labiodental_affricate.ogg/Voiced_labiodental_affricate.ogg.mp3',
  t̪θ: '/b/ba/Voiceless_dental_non-sibilant_affricate.oga/Voiceless_dental_non-sibilant_affricate.oga.mp3',
  d̪ð: '/2/27/Voiced_dental_non-sibilant_affricate.oga/Voiced_dental_non-sibilant_affricate.oga.mp3',
  ts: '/9/9d/Voiceless_alveolar_sibilant_affricate.oga/Voiceless_alveolar_sibilant_affricate.oga.mp3',
  dz: '/d/d8/Voiced_alveolar_sibilant_affricate.oga/Voiced_alveolar_sibilant_affricate.oga.mp3',
  t̠ʃ: '/9/97/Voiceless_palato-alveolar_affricate.ogg/Voiceless_palato-alveolar_affricate.ogg.mp3',
  d̠ʒ: '/e/e6/Voiced_palato-alveolar_affricate.ogg/Voiced_palato-alveolar_affricate.ogg.mp3',
  ʈʂ: '/e/e9/Voiceless_retroflex_affricate.ogg/Voiceless_retroflex_affricate.ogg.mp3',
  ɖʐ: '/5/58/Voiced_retroflex_affricate.ogg/Voiced_retroflex_affricate.ogg.mp3',
  tɕ: '/c/c4/Voiceless_alveolo-palatal_affricate.ogg/Voiceless_alveolo-palatal_affricate.ogg.mp3',
  dʑ: '/c/c1/Voiced_alveolo-palatal_affricate.ogg/Voiced_alveolo-palatal_affricate.ogg.mp3',
  cç: '/f/fd/Voiceless_palatal_affricate.ogg/Voiceless_palatal_affricate.ogg.mp3',
  ɟʝ: '/a/aa/Voiced_palatal_affricate.ogg/Voiced_palatal_affricate.ogg.mp3',
  kx: '/e/e5/Voiceless_velar_affricate.ogg/Voiceless_velar_affricate.ogg.mp3',
  ɡɣ: '/7/75/Voiced_velar_affricate.ogg/Voiced_velar_affricate.ogg.mp3',
  qχ: '/4/43/Voiceless_uvular_affricate.ogg/Voiceless_uvular_affricate.ogg.mp3',
  ɢʁ: '/3/35/Voiced_uvular_affricate.ogg/Voiced_uvular_affricate.ogg.mp3',
  ʔh: '/4/44/Voiceless_glottal_affricate.ogg/Voiceless_glottal_affricate.ogg.mp3',
  ʡʜ: '/e/ea/Voiceless_epiglottal_affricate.ogg/Voiceless_epiglottal_affricate.ogg.mp3',
  ʡʢ: '/e/e4/Voiced_epiglottal_affricate.ogg/Voiced_epiglottal_affricate.ogg.mp3',

  tɬ: '/9/9f/Voiceless_alveolar_lateral_affricate.ogg/Voiceless_alveolar_lateral_affricate.ogg.mp3',
  dɮ: '/3/31/Voiced_alveolar_lateral_affricate.ogg/Voiced_alveolar_lateral_affricate.ogg.mp3',
  tꞎ: '/d/db/Voiceless_retroflex_lateral_affricate.ogg/Voiceless_retroflex_lateral_affricate.ogg.mp3',
  'k𝼄': '/0/02/Voiceless_velar_lateral_affricate.ogg/Voiceless_velar_lateral_affricate.ogg.mp3',
  ɡʟ̝: '/9/93/Voiced_velar_lateral_affricate.ogg/Voiced_velar_lateral_affricate.ogg.mp3',

  ʈʼ: '/b/bc/Retroflex_ejective.ogg/Retroflex_ejective.ogg.mp3',
  cʼ: '/0/0f/Palatal_ejective.ogg/Palatal_ejective.ogg.mp3',
  qʼ: '/0/08/Uvular_ejective_plosive.ogg/Uvular_ejective_plosive.ogg.mp3',
  ɸʼ: '/7/70/Bilabial_ejective_fricative.ogg/Bilabial_ejective_fricative.ogg.mp3',
  fʼ: '/8/81/Labiodental_ejective_fricative.ogg/Labiodental_ejective_fricative.ogg.mp3',
  θʼ: '/2/22/Dental_ejective_fricative.ogg/Dental_ejective_fricative.ogg.mp3',
  ʃʼ: '/7/75/Palato-alveolar_ejective_fricative.ogg/Palato-alveolar_ejective_fricative.ogg.mp3',
  ʂʼ: '/d/d9/Retroflex_ejective_fricative.ogg/Retroflex_ejective_fricative.ogg.mp3',
  ɕʼ: '/3/37/Alveolo-palatal_ejective_fricative.ogg/Alveolo-palatal_ejective_fricative.ogg.mp3',
  xʼ: '/c/c6/Velar_ejective_fricative.ogg/Velar_ejective_fricative.ogg.mp3',
  χʼ: '/6/69/Uvular_ejective_fricative.ogg/Uvular_ejective_fricative.ogg.mp3',

  ᶑ: '/c/c8/Voiced-retroflex-implosive.ogg/Voiced-retroflex-implosive.ogg.mp3',
  ɓ̥: '/e/e8/Voiceless-bilabial-implosive.ogg/Voiceless-bilabial-implosive.ogg.mp3',
  ɗ̥: '/f/f2/Voiceless-alveolar-implosive.ogg/Voiceless-alveolar-implosive.ogg.mp3',
  ʄ̥: '/f/fc/Voiceless_palatal_implosive.ogg/Voiceless_palatal_implosive.ogg.mp3',
  ɠ̥: '/c/cc/Voiceless-velar-implosive.ogg/Voiceless-velar-implosive.ogg.mp3',
  ʛ̥: '/3/32/Voiceless-uvular-implosive.ogg/Voiceless-uvular-implosive.ogg.mp3',

  e̞: '/e/e0/Mid_front_unrounded_vowel.ogg/Mid_front_unrounded_vowel.ogg.mp3',
  ø̞: '/e/ee/Mid_front_rounded_vowel.ogg/Mid_front_rounded_vowel.ogg.mp3',
  ɤ̞: '/d/d8/%C9%A4%CC%9E_IPA_sound.opus/%C9%A4%CC%9E_IPA_sound.opus.mp3',
  o̞: '/a/a6/Mid_back_rounded_vowel.ogg/Mid_back_rounded_vowel.ogg.mp3',
  ä: '/5/50/Open_central_unrounded_vowel.ogg/Open_central_unrounded_vowel.ogg.mp3',
  ɝ: 'https://upload.wikimedia.org/wikipedia/commons/e/e1/En-us-er.ogg',
};
const wpBaseURL = 'https://upload.wikimedia.org/wikipedia/commons/transcoded';
